import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, CheckCircle } from 'lucide-react';
import { leadsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { LP_URL } from '../../lib/utils';

export const LeadForm = ({
  title = 'Receba conteúdos estratégicos',
  description = 'Cadastre-se para receber dicas e estratégias para sua clínica.',
  source = 'website',
  showClinicField = true,
  onSuccess,
  className = '',
  variant = 'default', // default, compact
}) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    clinic_name: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await leadsAPI.create({
        ...formData,
        source,
      });
      
      setSubmitted(true);
      toast.success('Cadastro realizado com sucesso!');
      
      if (onSuccess) {
        onSuccess();
      } else {
        // Default: redirect to WhatsApp after short delay
        setTimeout(() => {
          window.open(LP_URL, '_blank');
        }, 1500);
      }
    } catch (error) {
      toast.error('Erro ao enviar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={`bg-white rounded-xl p-8 text-center ${className}`} data-testid="lead-form-success">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
          Obrigado pelo interesse!
        </h3>
        <p className="text-slate-600">
          Em breve entraremos em contato.
        </p>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} className={`flex flex-col sm:flex-row gap-3 ${className}`} data-testid="lead-form-compact">
        <Input
          type="email"
          name="email"
          placeholder="Seu melhor e-mail"
          value={formData.email}
          onChange={handleChange}
          required
          className="flex-1"
          data-testid="lead-email-input"
        />
        <Button type="submit" className="btn-primary" disabled={loading} data-testid="lead-submit-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Inscrever-se'}
        </Button>
      </form>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-lg p-6 md:p-8 ${className}`} data-testid="lead-form">
      {title && (
        <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
          {title}
        </h3>
      )}
      {description && (
        <p className="text-slate-600 text-sm mb-6">
          {description}
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="name" className="text-sm font-medium text-slate-700">
            Nome *
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="Seu nome completo"
            value={formData.name}
            onChange={handleChange}
            required
            className="mt-1"
            data-testid="lead-name-input"
          />
        </div>

        <div>
          <Label htmlFor="email" className="text-sm font-medium text-slate-700">
            E-mail *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="seu@email.com"
            value={formData.email}
            onChange={handleChange}
            required
            className="mt-1"
            data-testid="lead-email-input"
          />
        </div>

        <div>
          <Label htmlFor="whatsapp" className="text-sm font-medium text-slate-700">
            WhatsApp
          </Label>
          <Input
            id="whatsapp"
            name="whatsapp"
            type="tel"
            placeholder="(00) 00000-0000"
            value={formData.whatsapp}
            onChange={handleChange}
            className="mt-1"
            data-testid="lead-whatsapp-input"
          />
        </div>

        {showClinicField && (
          <div>
            <Label htmlFor="clinic_name" className="text-sm font-medium text-slate-700">
              Nome da clínica/consultório
            </Label>
            <Input
              id="clinic_name"
              name="clinic_name"
              type="text"
              placeholder="Nome da sua clínica"
              value={formData.clinic_name}
              onChange={handleChange}
              className="mt-1"
              data-testid="lead-clinic-input"
            />
          </div>
        )}

        <Button
          type="submit"
          className="btn-primary w-full py-6"
          disabled={loading}
          data-testid="lead-submit-button"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Enviando...
            </>
          ) : (
            'Receber conteúdos e estratégias'
          )}
        </Button>
      </form>
    </div>
  );
};

export default LeadForm;
